// "What to work on" panel for /voice-profile. Surfaces actionable behaviour
// patterns from the user's recent meetings: hedges, filler, talk-share,
// listening, question quality, etc. Each insight is a Mercury-VP rule:
// what's happening + what to do differently.
//
// Insights are scored by priority and only the top N surface — we don't
// want to dump fourteen behaviours on the user. The point is the next move.

import type { BehaviourPattern } from "../lib/api";

export type Insight = {
  id: string;
  priority: number;
  headline: string;
  body: string;
  action: string;
};

export function ThingsToWorkOn({ insights }: { insights: Insight[] }) {
  if (insights.length === 0) {
    return (
      <section style={{ marginTop: 24 }}>
        <div className="ttwo-h">
          <h3>What to work on.</h3>
          <span className="meta" style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, letterSpacing: "0.12em", color: "rgb(var(--color-ink-muted))", textTransform: "uppercase", fontWeight: 500 }}>nothing flagged</span>
        </div>
        <p style={{ margin: 0, fontSize: 14, color: "rgb(var(--color-ink-soft))", lineHeight: 1.5 }}>
          Your patterns look healthy across the board. Keep the cadence going.
        </p>
      </section>
    );
  }

  return (
    <section style={{ marginTop: 24 }}>
      <div className="ttwo-h">
        <h3>What to work on.</h3>
        <span className="meta" style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, letterSpacing: "0.12em", color: "rgb(var(--color-ink-muted))", textTransform: "uppercase", fontWeight: 500 }}>
          {insights.length} {insights.length === 1 ? "thing" : "things"}
        </span>
      </div>
      <div className="ttwo-list">
        {insights.map((insight, i) => (
          <article key={insight.id} className="ttwo-item">
            <span className="num">{String(i + 1).padStart(2, "0")}</span>
            <div>
              <h4>{insight.headline}</h4>
              <p className="body">{insight.body}</p>
              <p className="action">{insight.action}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Insight templates. Each template fires only when the data crosses a
// threshold. Priority ranks how urgent the issue is so the top N surface.
// We use the user's actual averages so the body line is grounded.

export function buildInsights(perBehaviour: BehaviourPattern[]): Insight[] {
  const byName = new Map(perBehaviour.map((b) => [b.name, b]));
  const out: Insight[] = [];
  const round1 = (n: number) => n.toFixed(1);
  const round0 = (n: number) => Math.round(n).toString();

  const hedge = byName.get("position_weighted_hedges");
  if (hedge && hedge.averageCount >= 4) {
    out.push({
      id: "hedges",
      priority: hedge.averageCount * 2,
      headline: "You hedge before the ask",
      body: `"Sort of", "I think", "maybe" softened your asks ${round1(hedge.averageCount)} times per call on average. They drop the weight off your recommendation.`,
      action: "Lead with the recommendation as a clean sentence. Add context only if asked.",
    });
  }

  const filler = byName.get("filler_words");
  if (filler && filler.averageCount >= 5) {
    out.push({
      id: "filler",
      priority: filler.averageCount,
      headline: "Cut 'um' and 'uh'",
      body: `Filler words averaged ${round1(filler.averageCount)} per call. They surface when the next thought isn't fully formed yet.`,
      action: "Pause silently between sentences. The space lets the room catch up and your next line land harder.",
    });
  }

  const talk = byName.get("talk_time_ratio");
  if (talk && talk.averageCount >= 60) {
    out.push({
      id: "talk-share",
      priority: (talk.averageCount - 50) * 2,
      headline: "You're talking too much in 1:1s",
      body: `Talk-share averaged ${round0(talk.averageCount)}%. On 1:1s the target is closer to 50%, on standups closer to 35%.`,
      action: "After you answer, count to two before adding more. Let them carry the next sentence.",
    });
  }

  const monologue = byName.get("longest_monologue");
  if (monologue && monologue.averageCount > 180) {
    out.push({
      id: "monologue",
      priority: 8,
      headline: "Break the monologue",
      body: `Your longest stretch averaged ${round0(monologue.averageCount)} seconds. Anything past 90 seconds loses listeners.`,
      action: "After a point lands, pause and check: 'does that match what you're seeing?' Lets them back in.",
    });
  }

  const interrupt = byName.get("interruptions");
  if (interrupt && interrupt.averageCount >= 1.5) {
    out.push({
      id: "interruptions",
      priority: interrupt.averageCount * 3,
      headline: "Stop cutting in",
      body: `You interrupt others around ${round1(interrupt.averageCount)} times per call. Ideas come faster than turns sometimes.`,
      action: "When the urge hits, write the thought down. Bring it back after they finish.",
    });
  }

  const patience = byName.get("patience");
  if (patience && patience.averageCount < 5) {
    out.push({
      id: "patience",
      priority: 7,
      headline: "Hold the silence",
      body: `Patience signals: ${round1(patience.averageCount)} per call. You fill pauses faster than you need to.`,
      action: "After a question, count to four before re-prompting. The answer usually surfaces in that gap.",
    });
  }

  const listen = byName.get("active_listening");
  if (listen && listen.averageCount < 5) {
    out.push({
      id: "listening",
      priority: 6,
      headline: "Echo what they said",
      body: `Active-listening signals averaged ${round1(listen.averageCount)} per call. The benchmark is 5+.`,
      action: "Repeat the last 3-5 words of what they said before responding. 'You said the timeline matters most…'",
    });
  }

  const qq = byName.get("question_quality");
  if (qq && qq.averageCount < 3) {
    out.push({
      id: "question-quality",
      priority: 6,
      headline: "Ask better questions",
      body: `Open-ended questions: ${round1(qq.averageCount)} per call. Closed questions get short answers.`,
      action: "Replace 'is this OK?' with 'what does success look like for you?' Bigger answers come back.",
    });
  }

  const spec = byName.get("specificity");
  if (spec && spec.averageCount < 4) {
    out.push({
      id: "specificity",
      priority: 5,
      headline: "Get more specific",
      body: `Specific examples per call: ${round1(spec.averageCount)}. Vague answers slip past without traction.`,
      action: "Use a number, a date, or a name in your next answer. Specific lands; general fades.",
    });
  }

  const commits = byName.get("commitments");
  if (commits && commits.averageCount < 1) {
    out.push({
      id: "commitments",
      priority: 7,
      headline: "Close with a commitment",
      body: `Explicit commitments: ${round1(commits.averageCount)} per call. Conversations end without a next move.`,
      action: "End with: 'so the action is X by Y.' Make it crisp enough for them to repeat.",
    });
  }

  const weak = byName.get("weak_openers");
  if (weak && weak.averageCount >= 2) {
    out.push({
      id: "weak-openers",
      priority: weak.averageCount * 2,
      headline: "Open stronger",
      body: `Openers like "I just", "sorry to bother", "this might be silly" averaged ${round1(weak.averageCount)} per call. They shrink your presence before the ask lands.`,
      action: "Skip the apology opener. Start with the thing: 'I want to discuss X.'",
    });
  }

  const premature = byName.get("premature_agreement");
  if (premature && premature.averageCount >= 1) {
    out.push({
      id: "premature",
      priority: 6,
      headline: "Don't agree too fast",
      body: `You agreed before exploring the trade-off ${round1(premature.averageCount)} times per call. It can cut a useful thread early.`,
      action: "When you hear an idea, ask 'what's the trade-off?' before agreeing or disagreeing.",
    });
  }

  const pace = byName.get("speaking_pace");
  if (pace && pace.averageCount > 180) {
    out.push({
      id: "pace-fast",
      priority: 5,
      headline: "Slow your pace",
      body: `Speaking pace: ${round0(pace.averageCount)} wpm. The healthy range is 150-180.`,
      action: "Take a half-breath at every comma. The room will follow you instead of chasing.",
    });
  } else if (pace && pace.averageCount > 0 && pace.averageCount < 130) {
    out.push({
      id: "pace-slow",
      priority: 4,
      headline: "Pick up the pace",
      body: `Speaking pace: ${round0(pace.averageCount)} wpm. Below the healthy range of 150-180.`,
      action: "Don't rehearse mid-sentence. Pick the word you've got and keep moving.",
    });
  }

  const confidence = byName.get("confidence_markers");
  if (confidence && confidence.averageCount > 6) {
    out.push({
      id: "confidence",
      priority: 4,
      headline: "Soften certainty markers",
      body: `Words like 'definitely', 'absolutely', '100%' averaged ${round1(confidence.averageCount)} per call. Overuse can sound performative.`,
      action: "Reserve them for the things you're truly certain about. Try 'I think' for the rest.",
    });
  }

  return out.sort((a, b) => b.priority - a.priority).slice(0, 5);
}
